"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  ResultBanner,
  PrintButton,
  SegmentedControl,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* POWRA — point of work risk assessment (a.k.a. dynamic / pre-start check).
   A brief on-the-spot check the operative runs immediately before starting,
   to confirm that what's in front of them matches the assumptions in the RAMS
   and to catch anything that has changed on the day. It supports the
   "suitable and sufficient" risk-assessment duty (MHSWR 1999) for variable
   work — it never replaces the RAMS, and the stop/go call belongs to the
   people on site and their supervisor. */

type Answer = "yes" | "no" | "na";

interface Check {
  id: string;
  text: string;
  /** A "no" here is a hard stop, not just an amber fix-first. */
  critical?: boolean;
}

interface Section {
  id: string;
  title: string;
  hint: string;
  checks: Check[];
}

const SECTIONS: Section[] = [
  {
    id: "paperwork",
    title: "Paperwork & briefing",
    hint: "The job is covered, understood and authorised before anyone lifts a tool.",
    checks: [
      { id: "rams-read", text: "RAMS read and briefed to everyone doing the task?" },
      { id: "permits", text: "Permits in place where the work needs them (hot works, dig, confined space, isolation)?" },
      { id: "induction", text: "Site induction done for everyone on the team?" },
    ],
  },
  {
    id: "area",
    title: "The work area now",
    hint: "What you can actually see in front of you, not what the plan assumed.",
    checks: [
      { id: "access", text: "Access and egress clear and safe (including the emergency route)?" },
      { id: "lighting", text: "Lighting adequate for the task?" },
      { id: "housekeeping", text: "Housekeeping acceptable — no trip, slip or fire-load build-up?" },
      { id: "segregation", text: "Public and other trades segregated from your work zone?" },
      { id: "weather", text: "Weather suitable for the task (wind, rain, ice, heat)?" },
    ],
  },
  {
    id: "services",
    title: "Services & hidden dangers",
    hint: "The things that injure people when they are assumed rather than checked.",
    checks: [
      { id: "services-located", text: "Buried and overhead services located and marked (drawings, CAT scan, spotting)?", critical: true },
      { id: "isolation", text: "Isolations confirmed by prove dead — live, dead, live?", critical: true },
      { id: "asbestos", text: "Asbestos register checked for any pre-2000 building before disturbing fabric?", critical: true },
    ],
  },
  {
    id: "plant",
    title: "Plant, tools & kit",
    hint: "Right kit, in date, in good order, ready before the task starts.",
    checks: [
      { id: "tools", text: "Right tools available and inspected (no damage, in test date)?" },
      { id: "guards", text: "Guards and safety devices in place and working?" },
      { id: "lifting", text: "Lifting kit (slings, chains, eyes) in date and undamaged?" },
      { id: "ppe", text: "PPE on hand, serviceable and the right type for the task?" },
    ],
  },
  {
    id: "people",
    title: "People",
    hint: "The team is competent, fit and properly looked after for this task.",
    checks: [
      { id: "competent", text: "Everyone competent and authorised for THIS task?" },
      { id: "fit", text: "Everyone fit for work — no fatigue, illness, drink, drugs or medication concerns?" },
      { id: "new-starters", text: "New starters paired up and supervised?" },
      { id: "lone", text: "Lone-working plan in place and check-in agreed, if anyone works alone?" },
    ],
  },
  {
    id: "changes",
    title: "Changes from the RAMS",
    hint: "The whole point of a POWRA — what is different today from the planned task.",
    checks: [
      { id: "changed", text: "Has ANYTHING changed from what the RAMS describes — scope, conditions or method?", critical: true },
      { id: "new-hazards", text: "Any new hazards introduced by others working nearby?", critical: true },
    ],
  },
];

// "Changes" questions read the opposite way: a "yes" (something has changed /
// new hazards) is the unsafe answer. Treat those as the trigger.
const INVERTED = new Set(["changed", "new-hazards"]);

/** Does this answer count as a "flag" (something to fix or escalate)? */
function isFlag(check: Check, answer: Answer | undefined): boolean {
  if (answer === undefined) return false;
  if (INVERTED.has(check.id)) return answer === "yes";
  return answer === "no";
}

const ANSWER_LABELS: { value: Answer; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "na", label: "N/A" },
];

function ToggleGroup({
  check,
  value,
  onChange,
}: {
  check: Check;
  value: Answer | undefined;
  onChange: (a: Answer) => void;
}) {
  // For inverted questions, swap which choices are styled "good" vs "bad".
  const goodValue: Answer = INVERTED.has(check.id) ? "no" : "yes";
  const badValue: Answer = INVERTED.has(check.id) ? "yes" : "no";

  const flagged = isFlag(check, value);

  return (
    <div
      className={`rounded-lg border px-3.5 py-3 transition-colors ${
        flagged ? "border-risk-veryhigh/45 bg-[#fdecec]" : "border-line bg-white"
      }`}
    >
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <span className="text-[0.92rem] leading-snug text-body">
          {check.text}
          {check.critical && (
            <span className="ml-2 align-middle text-[0.68rem] font-bold uppercase tracking-wide text-risk-veryhigh">
              stop check
            </span>
          )}
        </span>
        <SegmentedControl
          legend={check.text}
          name={`powra-${check.id}`}
          value={value}
          onChange={onChange}
          className="shrink-0"
          options={ANSWER_LABELS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            activeCls:
              opt.value === goodValue
                ? "border-emerald bg-emerald-tint text-emerald-deep"
                : opt.value === badValue
                  ? "border-risk-veryhigh bg-[#fdecec] text-[#a31616]"
                  : "border-ink bg-ink text-white",
          }))}
        />
      </div>
    </div>
  );
}

interface Outcome {
  level: Verdict;
  decision: "go" | "fix-first" | "stop";
  title: string;
  advice: string;
}

export function PowraTool() {
  const [site, setSite] = useState("");
  const [task, setTask] = useState("");
  const [operatives, setOperatives] = useState("");
  const [supervisor, setSupervisor] = useState("");
  const [ramsRef, setRamsRef] = useState("");
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const resultTracked = useRef<string | null>(null);

  const setAnswer = (id: string, a: Answer) => setAnswers((prev) => ({ ...prev, [id]: a }));

  const allChecks = useMemo(() => SECTIONS.flatMap((s) => s.checks), []);
  const answeredCount = allChecks.filter((c) => answers[c.id] !== undefined).length;
  const totalCount = allChecks.length;
  const complete = answeredCount === totalCount;

  const flagged = useMemo(
    () => allChecks.filter((c) => isFlag(c, answers[c.id])),
    [allChecks, answers],
  );
  const criticalFlags = flagged.filter((c) => c.critical);

  const outcome: Outcome | null = useMemo(() => {
    if (!complete) return null;
    if (criticalFlags.length > 0) {
      return {
        level: "veryhigh",
        decision: "stop",
        title: "STOP — do not start work",
        advice:
          "A critical check has failed (services, isolation, asbestos or a change from the RAMS). Do not start. Make the area safe, escalate to your supervisor, and treat the RAMS as needing review before anyone proceeds — what is in front of you no longer matches the planned task.",
      };
    }
    if (flagged.length > 0) {
      return {
        level: flagged.length >= 3 ? "high" : "medium",
        decision: "fix-first",
        title: `Fix ${flagged.length} item${flagged.length > 1 ? "s" : ""} before you start`,
        advice:
          "Work cannot start safely as things stand. Put the items below right, then re-run the check. If you cannot resolve one yourself, raise it with your supervisor before proceeding.",
      };
    }
    return {
      level: "low",
      decision: "go",
      title: "Good to go",
      advice:
        "All checks are clear. Proceed with the task as set out in the RAMS — and stop and re-check the moment conditions, the team or the method change.",
    };
  }, [complete, criticalFlags.length, flagged]);

  // Fire the funnel event once per distinct decision.
  useEffect(() => {
    if (outcome && resultTracked.current !== outcome.decision) {
      resultTracked.current = outcome.decision;
      trackToolResult("powra", { decision: outcome.decision });
    }
  }, [outcome]);

  const now = new Date();
  const dateLabel = now.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const timeLabel = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* Inputs */}
      <div className="space-y-5">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Today&apos;s details</h2>
          <p className="mt-1 text-sm text-muted">
            Date and time are filled in automatically when you print the card.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Site / location">
              <RememberedTextInput store="sites" value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. Plot 14, Elm Park" />
            </Field>
            <Field label="Task">
              <TextInput value={task} onChange={(e) => setTask(e.target.value)} placeholder="e.g. First-fix wiring, plot kitchen" />
            </Field>
            <Field label="Operative name(s)" className="sm:col-span-2">
              <TextInput
                value={operatives}
                onChange={(e) => setOperatives(e.target.value)}
                placeholder="Everyone doing the task"
              />
            </Field>
            <Field label="Supervisor">
              <RememberedTextInput store="people" value={supervisor} onChange={(e) => setSupervisor(e.target.value)} placeholder="Who to escalate to" />
            </Field>
            <Field label="RAMS reference" hint="The RAMS this task sits under.">
              <TextInput value={ramsRef} onChange={(e) => setRamsRef(e.target.value)} placeholder="e.g. RAMS-2026-014 rev B" />
            </Field>
          </div>
          <div className="mt-4">
            <RememberedNamesNote stores={["people", "sites"]} />
          </div>
        </div>

        {SECTIONS.map((section, i) => {
          const sectionFlags = section.checks.filter((c) => isFlag(c, answers[c.id])).length;
          return (
            <div key={section.id} className="card shadow-soft p-6">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-lg">
                  <span className="text-muted">{i + 1}.</span> {section.title}
                </h2>
                {sectionFlags > 0 && (
                  <span className="rounded-full bg-[#fdecec] px-2.5 py-0.5 text-[0.72rem] font-bold uppercase tracking-wide text-[#a31616]">
                    {sectionFlags} to fix
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-muted">{section.hint}</p>
              <div className="mt-4 space-y-2.5">
                {section.checks.map((check) => (
                  <ToggleGroup
                    key={check.id}
                    check={check}
                    value={answers[check.id]}
                    onChange={(a) => setAnswer(check.id, a)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Result + printable card */}
      <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="powra-card">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Point of work risk assessment
              </div>
              <h2 className="mt-1 text-lg">
                {task || "Pre-start check"}
                {site ? ` — ${site}` : ""}
              </h2>
              <div className="mt-0.5 text-[0.8rem] text-muted">
                {dateLabel} · {timeLabel}
                {ramsRef ? ` · under ${ramsRef}` : ""}
              </div>

              <div className="mt-4">
                <ResultBanner level={outcome.level} title={outcome.title}>
                  {outcome.advice}
                </ResultBanner>
              </div>

              {flagged.length > 0 && (
                <div className="mt-4">
                  <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                    {outcome.decision === "stop" ? "Reasons to stop" : "Fix before starting"}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {flagged.map((c) => (
                      <li key={c.id} className="flex items-start gap-2 text-[0.86rem] leading-snug text-ink">
                        <span
                          className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                            c.critical ? "bg-risk-veryhigh" : "bg-risk-high"
                          }`}
                        />
                        <span>
                          {c.text}
                          {c.critical && <span className="ml-1 font-semibold text-risk-veryhigh">(stop check)</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Record header for the printed card */}
              <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-[0.82rem]">
                <div>
                  <dt className="text-muted">Operative(s)</dt>
                  <dd className="font-semibold text-ink">{operatives || "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted">Supervisor</dt>
                  <dd className="font-semibold text-ink">{supervisor || "—"}</dd>
                </div>
              </dl>

              {/* Sign-off block */}
              <div className="mt-5 border-t border-line pt-4">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Sign-off</div>
                <p className="mt-1 text-[0.78rem] leading-relaxed text-muted">
                  By signing, those named confirm they ran this check and that the decision to proceed, fix or
                  stop was made on site.
                </p>
                <div className="mt-3 space-y-4 text-[0.82rem]">
                  <div>
                    <div className="text-muted">Operative signature</div>
                    <div className="mt-5 border-b border-ink/50" />
                  </div>
                  <div>
                    <div className="text-muted">Operative signature</div>
                    <div className="mt-5 border-b border-ink/50" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-muted">Supervisor signature</div>
                      <div className="mt-5 border-b border-ink/50" />
                    </div>
                    <div>
                      <div className="text-muted">Time</div>
                      <div className="mt-5 border-b border-ink/50" />
                    </div>
                  </div>
                </div>
              </div>

              <PrintFooter toolName="point of work risk assessment" />
            </div>

            <div className="flex justify-end">
              <PrintButton tool="powra" label="Print / save card as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer every check above ({answeredCount} of {totalCount} done) to get the stop / go decision and a
            printable, signable card.
          </div>
        )}
      </div>
    </div>
  );
}
